/**
 * Compliance ▸ Vessel Certificates metinleri (plan M7 Task 27a, 27b; ADR 0029 K28–K31, K34):
 * ağaç, liste, ayrıntı, Survey Schedule, Regulatory Requirements, Certificate Calendar, gemi
 * diyalogları ve ofis formları. Uçların hata kodları (`VESSEL_CERTIFICATE_ERROR_CODES`) iki
 * dilde; `locales.test.ts` sınar [NR496 4.1.5].
 */

export const vesselCertificatesEn = {
  description: 'Statutory, class and flag certificates, surveys and class records of the vessel.',
  error: 'Certificates could not be loaded',
  retry: 'Try again',
  noVessel: 'No vessel yet',
  categories: {
    statutory: 'Statutory Certificates',
    class: 'Class Certificates',
    safety: 'Safety Certificates',
    environmental: 'Environmental Certificates',
    flag_port: 'Flag / Port Certificates',
    other: 'Other Certificates',
  },
  badges: {
    solas: 'SOLAS',
    marpol: 'MARPOL',
    mlc: 'MLC',
    ism: 'ISM',
    isps: 'ISPS',
    environmental: 'Environmental',
    flag: 'Flag',
    class: 'Class',
    other: 'Other',
  },
  status: {
    valid: 'Valid',
    expiring: 'Expiring',
    expired: 'Overdue',
    superseded: 'Superseded',
    onBoard: 'Issued on board',
  },
  tree: {
    root: 'Certificate Tracking',
    label: 'Certificate tree',
    open: 'Certificate Tracking',
    count: '{{count}} certificates',
    close: 'Close',
  },
  card: {
    vessel: 'Vessel',
    imo: 'IMO {{imo}}',
    vesselType: 'Vessel Type',
    flag: 'Flag',
    port: 'Port',
    dateRange: 'Date Range',
    dateRangePlaceholder: 'Any expiry date',
  },
  toolbar: {
    search: 'Search certificate, type, status...',
    searchLabel: 'Search certificates',
    noResults: 'No certificate found',
    add: 'Add Certificate',
    issue: 'Record On-board Issuance',
    export: 'Export',
    exportHint: 'Export the certificates of the selected category to Excel or CSV',
    exportFailed: 'The file could not be prepared.',
  },
  list: {
    title: 'Certificate List – {{scope}}',
    allScope: 'All Certificates',
    filters: 'Certificate filters',
    type: 'Certificate Type',
    statusFilter: 'Status',
    due: 'Due Date',
    all: 'All',
    search: 'Search by name or number...',
    searchLabel: 'Search the list',
    statuses: {
      all: 'All',
      valid: 'Valid',
      expiring: 'Expiring',
      expired: 'Overdue',
      superseded: 'Superseded',
    },
    dueOptions: {
      all: 'All',
      overdue: 'Overdue',
      '30': 'Next 30 days',
      '90': 'Next 90 days',
      '365': 'Next 12 months',
      none: 'No expiry',
    },
    summary: {
      label: 'Certificate summary',
      valid: 'Valid',
      expiring: 'Expiring (≤ {{days}} days)',
      overdue: 'Overdue',
      total: 'Total',
    },
    columns: {
      no: 'No',
      name: 'Certificate / Document Name',
      type: 'Type',
      issuedBy: 'Issued By',
      issueDate: 'Issue Date',
      expiryDate: 'Expiry Date',
      status: 'Status',
      daysLeft: 'Days Left',
      actions: 'Actions',
    },
    table: 'Certificate list',
    sortBy: 'Sort by {{column}}',
    daysLeft: '{{count}} days left',
    daysOverdue: '{{count}} days overdue',
    noExpiry: 'No expiry',
    pendingOffice: 'Issued on board · awaiting office approval',
    history_one: 'History ({{count}})',
    history_other: 'History ({{count}})',
    historyToggle_one: 'Show the earlier certificate of {{name}} ({{count}})',
    historyToggle_other: 'Show the {{count}} earlier certificates of {{name}}',
    showing: 'Showing {{from}} – {{to}} of {{total}}',
    showingNone: 'Showing 0 of 0',
    pagination: 'Certificate list pages',
    pager: { first: 'First page', previous: 'Previous page', next: 'Next page', last: 'Last page' },
    view: 'View {{name}}',
    edit: 'Edit {{name}}',
    more: 'More actions for {{name}}',
    empty: 'No certificates yet',
    emptyOffice: 'Add the vessel’s certificates with “Add Certificate”.',
    emptyShip: 'The office enters the certificates; they arrive with the next sync.',
    emptyScope: 'No certificate in this category',
    noMatch: 'No certificate matches the filters',
  },
  upcoming: {
    title: 'Upcoming Certificates',
    viewAll: 'View All',
    table: 'Upcoming certificates',
    columns: {
      dueDate: 'Due Date',
      name: 'Certificate Name',
      type: 'Type',
      daysLeft: 'Days Left',
      status: 'Status',
    },
    kinds: {
      expiry: 'Expiry',
      annual: 'Annual survey',
      intermediate: 'Intermediate survey',
      renewal: 'Renewal survey',
    },
    tones: {
      expiring: 'Expiring',
      expired: 'Overdue',
      upcoming: 'Upcoming',
      open: 'Window open',
      overdue: 'Overdue',
    },
    empty: 'Nothing due in the next 90 days',
  },
  distribution: {
    title: 'Certificate Status Distribution',
    label: 'Certificate status distribution',
    total: 'Total',
    valid: 'Valid',
    expiring: 'Expiring',
    overdue: 'Overdue',
    value: '{{value}} ({{percent}}%)',
  },
  details: {
    title: 'Certificate Details',
    empty: 'Select a certificate to see its details',
    daysLeft: 'Days Left',
    tabs: {
      label: 'Certificate details',
      general: 'General',
      document: 'Document',
      history: 'History',
      remarks: 'Remarks',
    },
    fields: {
      number: 'Certificate No.',
      name: 'Certificate Name',
      type: 'Type',
      subCategory: 'Sub Category',
      issuedBy: 'Issued By',
      issueDate: 'Issue Date',
      expiryDate: 'Expiry Date',
      anniversary: 'Anniversary Date',
      nextSurvey: 'Next Survey',
      window: 'Survey Window',
      vessel: 'Vessel',
      status: 'Status',
      daysLeft: 'Days Left',
    },
    nextSurveyValue: '{{date}} · {{kind}}',
    windowValue: '{{from}} – {{to}}',
    windowStates: {
      upcoming: 'opens {{date}}',
      open: 'open until {{date}}',
      overdue: 'closed {{date}}',
    },
    overridden: 'Set by the office: {{reason}}',
    noExpiry: 'No expiry',
    onBoardNote:
      'Issued on board by the surveyor and sent to the office for approval. It counts as valid until the office approves it; then it is the vessel’s certificate and the replaced one moves to History.',
    supersededNote: 'Renewed or replaced: this certificate is history and is not counted.',
    pages: {
      title: 'Scanned pages',
      page: 'Page {{n}}',
      empty: 'No scanned pages',
      view: 'View {{name}}',
      download: 'Download {{name}}',
      viewAll: 'View document',
      onBoard: {
        on_board: 'On board',
        downloading: 'Downloading',
        not_on_board: 'Not on board',
      },
      queued: 'Queued for download',
      queuedReason: 'Queued for download: {{reason}}',
    },
    history: {
      chain: 'Renewal chain',
      current: 'This certificate',
      deleted: 'Deleted',
      endorsements: 'Endorsements',
      noEndorsements: 'No endorsements recorded',
      endorsementLine: '{{kind}} · {{place}} · {{surveyor}}',
      satisfies: 'Satisfies the {{kind}} window ({{from}} – {{to}})',
      outside: 'Outside the survey window: recorded, does not satisfy it',
      additional: 'Additional survey',
      issuance: 'Issued on board',
      issuanceLine: '{{number}} · {{issuedBy}} · valid to {{date}}',
      shortTerm: 'Short-term',
      audit: 'Audit trail',
      openAudit: 'Show audit trail',
      auditTitle: 'Audit trail — {{name}}',
      officeHistory: 'The field history of this certificate is kept in the office.',
    },
    remarks: {
      empty: 'No remarks yet',
      by: '{{person}} · {{date}}',
    },
  },
  attached: {
    title: 'Attached Documents',
    empty: 'No scanned pages',
    download: 'Download {{name}}',
  },
  quickActions: {
    title: 'Quick Actions',
    viewDocument: 'View Document',
    download: 'Download',
    addRemark: 'Add Remark',
    endorse: 'Record Endorsement',
    reminder: 'Create Reminder',
    approve: 'Review & approve',
  },
  approval: {
    title: 'Certificates issued on board',
    officeTitle_one: '{{count}} certificate issued on board awaits office approval',
    officeTitle_other: '{{count}} certificates issued on board await office approval',
    shipTitle_one: '{{count}} certificate issued on board · sent to the office for approval',
    shipTitle_other: '{{count}} certificates issued on board · sent to the office for approval',
    shipNote: 'Sent to the office for approval',
    noRight: 'Approval needs the right to add certificates',
    review: 'Review & approve',
    dialogTitle: 'Approve on-board issuance',
    approve: 'Approve',
    approved: 'Approved: {{name}} is now in the certificate list.',
    hint: 'The office certificate is created from this record and sent to the vessel. You can edit it afterwards.',
    hintReplaces:
      'The office certificate is created from this record and sent to the vessel; the replaced certificate moves to History (it is not deleted). You can edit it afterwards.',
    fields: {
      name: 'Certificate',
      number: 'Number',
      issuedBy: 'Issued By',
      issueDate: 'Issue Date',
      expiryDate: 'Expiry Date',
      replaces: 'Replaces',
    },
    bell: 'Certificate issued on board',
  },
  reminder: {
    title: 'Reminders',
    body: 'Reminders are automatic: notices go out 90, 60 and 30 days before the expiry date. Personal reminders are not used.',
    stages: 'Notice stages',
    stage: '{{days}} days before expiry',
    reached: 'Reached',
    pending: 'Pending',
    expired: 'The certificate has expired: the overdue notice is active.',
    none: 'No notice yet ({{days}} days left).',
    current: 'Current stage: {{days}} days before expiry.',
    noExpiry: 'This certificate has no expiry date.',
    close: 'Close',
  },
  quickLinks: {
    title: 'Quick Links',
    add: 'Add New Certificate',
    issue: 'Record On-board Issuance',
    calendar: 'Certificate Calendar',
    surveys: 'Survey Schedule',
    regulatory: 'Regulatory Requirements',
  },
  views: {
    back: 'Back to Certificate List',
  },
  viewer: {
    title: '{{name}} — page {{page}} of {{count}}',
    previous: 'Previous page',
    next: 'Next page',
    close: 'Close',
    notOnBoard: 'This page is not on board yet. It downloads with the queue.',
    printFooter:
      'Copy for reference — {{name}} No. {{number}} — Felixmate — printed {{at}} UTC by {{person}}, {{place}}',
  },
  surveys: {
    title: 'Survey Schedule',
    table: 'Survey schedule',
    columns: {
      survey: 'Survey',
      last: 'Last',
      next: 'Next',
      window: 'Window',
      status: 'Status',
      report: 'Report',
      actions: 'Actions',
    },
    kinds: {
      annual: 'Annual',
      intermediate: 'Intermediate',
      special: 'Special (Renewal)',
      bottom: 'Bottom',
      boiler: 'Boiler',
      safety_equipment: 'Safety Equipment',
      radio: 'Radio',
      tailshaft: 'Tailshaft',
      other: 'Other',
    },
    states: {
      upcoming: 'Upcoming',
      open: 'Window open',
      overdue: 'Overdue',
    },
    notRecorded: 'Not recorded',
    reportOnFile: 'On file',
    overridden: 'Window from the class status report: {{reason}}',
    add: 'Add Survey',
    edit: 'Edit {{kind}} survey',
    addKind: 'Add {{kind}} survey',
  },
  regulatory: {
    title: 'Regulatory Requirements',
    tabs: {
      label: 'Regulatory requirements',
      findings: 'Conditions of Class',
      items: 'Class Item List',
      due: 'Class Due List',
      authorizations: 'CE Authorizations',
    },
    findings: {
      table: 'Class findings',
      columns: {
        number: 'No',
        kind: 'Kind',
        text: 'Description',
        equipment: 'Equipment',
        imposed: 'Imposed',
        due: 'Due',
        closed: 'Closed',
        status: 'Status',
        actions: 'Actions',
      },
      kinds: {
        coc: 'Condition of Class',
        memorandum: 'Memorandum',
        recommendation: 'Recommendation',
      },
      states: { open: 'Open', closed: 'Closed', overdue: 'Overdue' },
      add: 'Add Finding',
      edit: 'Edit {{number}}',
      empty: 'No class findings',
    },
    items: {
      table: 'Class item list',
      columns: {
        code: 'Item Code',
        description: 'Description',
        category: 'Category',
        principle: 'Survey Principle',
        interval: 'Interval (months)',
        ce: 'Chief Engineer survey',
      },
      categories: {
        machinery: 'Machinery',
        electrical: 'Electrical',
        pressure_vessel: 'Pressure vessel',
        boiler: 'Boiler',
        tailshaft: 'Tailshaft',
        other: 'Other',
      },
      principles: {
        pms_ce: 'PMS (chief engineer)',
        surveyor: 'Surveyor',
        condition_monitoring: 'Condition monitoring',
        none: 'None',
      },
      ceAllowed: 'Allowed',
      ceNotAllowed: 'Not allowed',
      warnings: 'Equipment with a BV item code that is not on the list: {{codes}}',
      import: 'Import class item list',
      empty: 'No class items',
      emptyHint: 'Import the BV item list (CSV or XLSX).',
    },
    due: {
      table: 'Class due list',
      columns: {
        item: 'Item',
        equipment: 'Equipment',
        lastSurvey: 'Last CE survey',
        confirmation: 'Surveyor confirmation',
        due: 'Next due',
        state: 'Status',
      },
      states: {
        never_surveyed: 'Not surveyed',
        not_credited: 'Not credited',
        awaiting_confirmation: 'Awaiting confirmation',
        confirmed: 'Confirmed',
        overdue: 'Overdue',
      },
      notCredited: 'Not credited: {{reason}}',
      confirmedBy: '{{date}} · {{surveyor}}',
      confirm: 'Record Surveyor Confirmation',
      empty: 'No class items',
    },
    authorizations: {
      table: 'Chief engineer authorizations',
      columns: {
        person: 'Chief Engineer',
        from: 'Valid From',
        to: 'Valid To',
        ref: 'Declaration Ref.',
        state: 'Status',
        actions: 'Actions',
      },
      valid: 'Valid',
      notValid: 'Not valid',
      openEnded: 'Open-ended',
      unknownPerson: 'Not on board yet',
      add: 'Add Authorization',
      edit: 'Edit authorization {{ref}}',
      empty: 'No authorizations',
    },
  },
  calendar: {
    title: 'Certificate Calendar',
    previous: 'Previous month',
    next: 'Next month',
    today: 'Today',
    grid: 'Certificate calendar',
    legend: { expiry: 'Expiry', survey: 'Survey due' },
    agenda: 'Due this month',
    empty: 'Nothing due this month',
    weekdays: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    },
    dayEvents: '{{count}} due',
  },
  dialogs: {
    cancel: 'Cancel',
    close: 'Close',
    immutable: 'This record cannot be changed after it is saved.',
    endorsement: {
      title: 'Record Endorsement',
      certificate: 'Certificate',
      kind: 'Survey',
      kinds: { annual: 'Annual', intermediate: 'Intermediate', additional: 'Additional' },
      endorsedOn: 'Endorsed On',
      place: 'Place',
      surveyor: 'Surveyor',
      remarks: 'Remarks',
      scan: 'Scan (optional)',
      save: 'Record Endorsement',
      outside: 'Recorded outside the survey window: it does not satisfy the survey.',
      saved: 'Endorsement recorded.',
    },
    confirmation: {
      title: 'Record Surveyor Confirmation',
      surveyor: 'Surveyor',
      confirmedOn: 'Confirmed On',
      items: 'Items',
      itemOption: '{{code}} — {{description}} (CE survey {{date}})',
      noItems: 'No chief engineer survey is awaiting confirmation.',
      remarks: 'Remarks',
      scan: 'Signed report (optional)',
      save: 'Record Confirmation',
    },
    issuance: {
      title: 'Record On-board Issuance',
      hint: 'The surveyor issued this certificate on board. It counts as valid until the office enters it.',
      type: 'Certificate Type',
      replaces: 'Replaces',
      replacesNone: 'None (new certificate)',
      number: 'Certificate No.',
      issuedBy: 'Issued By',
      issueDate: 'Issue Date',
      expiryDate: 'Expiry Date',
      shortTerm: 'Short-term / interim certificate',
      remarks: 'Remarks',
      scan: 'Scan (optional)',
      save: 'Record Issuance',
    },
    scanFailed: 'The record is saved but the scan could not be attached: {{reason}}',
    remark: {
      title: 'Add Remark',
      text: 'Remark',
      hint: 'The remark is saved with today’s date and your name. Earlier remarks cannot be changed.',
      save: 'Add Remark',
    },
    certificate: {
      addTitle: 'Add Certificate',
      editTitle: 'Edit Certificate',
      sections: {
        identity: 'Certificate',
        dates: 'Dates',
        survey: 'Next survey (office override)',
        links: 'Links',
        pages: 'Scanned pages',
      },
      type: 'Certificate Type',
      number: 'Certificate No.',
      name: 'Certificate Name',
      issuedBy: 'Issued By',
      issueDate: 'Issue Date',
      expiryDate: 'Expiry Date',
      anniversaryDate: 'Anniversary Date',
      anniversaryHint: 'Empty: the day and month of the expiry date.',
      nextSurveyOverride: 'Next Survey',
      windowFromOverride: 'Window From',
      windowToOverride: 'Window To',
      overrideReason: 'Reason',
      overrideHint:
        'Leave empty to use the computed window. A date from the class or flag status report needs a reason.',
      renewedFrom: 'Renews',
      renewedNone: 'None',
      onBoard: 'Issued on board',
      onBoardNone: 'None',
      pagesHint: 'PDF, JPEG or PNG; up to 25 MB per page.',
      pagesAfterSave: 'Save the certificate first, then attach its scanned pages.',
      upload: 'Add pages',
      uploading: 'Uploading {{name}} ({{percent}}%)',
      moveUp: 'Move {{name}} up',
      moveDown: 'Move {{name}} down',
      removePage: 'Remove {{name}}',
      save: 'Save',
      remove: 'Remove certificate',
      removeTitle: 'Remove certificate',
      removeBody: 'Remove {{name}}? It stays in the history and the audit trail.',
      saved: 'Certificate saved. You can now attach its scanned pages.',
    },
    survey: {
      addTitle: 'Add Survey',
      editTitle: 'Edit Survey',
      kind: 'Survey',
      lastDoneOn: 'Last Done',
      nextDueOn: 'Next Due',
      windowFrom: 'Window From',
      windowTo: 'Window To',
      computed: 'Computed window: {{from}} – {{to}}',
      overrideReason: 'Reason',
      overrideHint: 'A window different from the computed one needs a reason.',
      remarks: 'Remarks',
      save: 'Save',
      remove: 'Remove survey',
    },
    finding: {
      addTitle: 'Add Class Finding',
      editTitle: 'Edit Class Finding',
      kind: 'Kind',
      number: 'No',
      text: 'Description',
      imposedOn: 'Imposed On',
      dueOn: 'Due Date',
      closedOn: 'Closed On',
      equipment: 'Equipment',
      equipmentNone: 'Not linked',
      equipmentHint: 'An open, overdue condition of class takes the equipment out of PMS.',
      save: 'Save',
      remove: 'Remove finding',
    },
    authorization: {
      addTitle: 'Add CE Authorization',
      editTitle: 'Edit CE Authorization',
      hint: 'Owner’s declaration authorising the chief engineer to survey machinery items [NR467 Pt A Ch 2 App 2].',
      person: 'Chief Engineer',
      validFrom: 'Valid From',
      validTo: 'Valid To',
      declarationRef: 'Declaration Ref.',
      save: 'Save',
      remove: 'Remove authorization',
    },
    import: {
      title: 'Import Class Item List',
      hint: 'CSV or XLSX with the columns Item Code, Description, Category, Survey Principle and Interval (months).',
      file: 'File',
      choose: 'Choose file',
      preview: 'Preview',
      apply: 'Import',
      rows: 'Rows read: {{count}}',
      added: 'To add: {{count}}',
      updated: 'To update: {{count}}',
      unchanged: 'Unchanged: {{count}}',
      tiles: {
        rows: 'Rows read',
        added: 'To add',
        updated: 'To update',
        unchanged: 'Unchanged',
        errors: 'Rows with errors',
      },
      missing: 'Not in the file (kept): {{codes}}',
      errorsTitle: 'Rows with errors: nothing is imported until they are fixed.',
      errorRow: 'Row {{row}}',
      errorColumns: { row: 'Row', column: 'Column', problem: 'Problem' },
      addedColumns: { row: 'Row', code: 'Item Code', description: 'Description' },
      applied: 'Imported: {{added}} added, {{updated}} updated.',
    },
  },
  fieldErrors: {
    REQUIRED: 'Required',
    TOO_LONG: 'Too long',
    INVALID_DATE: 'Enter a valid date',
    FUTURE_DATE: 'Cannot be in the future',
    EXPIRY_BEFORE_ISSUE: 'The expiry date is before the issue date',
    OVERRIDE_REASON_REQUIRED: 'Enter the reason (class or flag status report)',
    WINDOW_ORDER: 'The window ends before it starts',
    DUE_BEFORE_LAST: 'The next due date is before the last survey',
    DUE_BEFORE_IMPOSED: 'The due date is before the imposed date',
    CLOSED_BEFORE_IMPOSED: 'The closing date is before the imposed date',
    VALID_TO_BEFORE_FROM: 'The end is before the start',
    ITEMS_REQUIRED: 'Choose at least one item',
  },
  errors: {
    INVALID_CERTIFICATE_TYPE: 'The certificate type is invalid.',
    INVALID_CERTIFICATE: 'Some certificate fields are invalid.',
    INVALID_CERTIFICATE_PAGE: 'The scanned page is invalid.',
    INVALID_SURVEY: 'Some survey fields are invalid.',
    INVALID_CLASS_FINDING: 'Some class finding fields are invalid.',
    INVALID_CLASS_ITEM: 'Some class item fields are invalid.',
    INVALID_CE_AUTHORIZATION: 'Some authorization fields are invalid.',
    INVALID_ENDORSEMENT: 'Some endorsement fields are invalid.',
    INVALID_CLASS_CONFIRMATION: 'Some confirmation fields are invalid.',
    INVALID_ISSUANCE: 'Some fields of the certificate issued on board are invalid.',
    PAGE_FILE_FIXED: 'A page’s file cannot change; add the new scan as a new page.',
    CERTIFICATE_TYPE_IN_USE: 'The type has certificates and cannot be removed.',
    EXPIRY_BEFORE_ISSUE: 'The expiry date is before the issue date.',
    OVERRIDE_REASON_REQUIRED: 'A date different from the computed window needs a reason.',
    WINDOW_ORDER: 'The window ends before it starts.',
    DUE_BEFORE_LAST: 'The next due date is before the last survey.',
    DUE_BEFORE_IMPOSED: 'The due date is before the imposed date.',
    CLOSED_BEFORE_IMPOSED: 'The closing date is before the imposed date.',
    VALID_TO_BEFORE_FROM: 'The end of validity is before its start.',
    DUPLICATE_TYPE_CODE: 'This type code is already used.',
    DUPLICATE_ITEM_CODE: 'This item code is already on the list.',
    DUPLICATE_ITEM: 'The same survey is chosen twice.',
    UNKNOWN_CERTIFICATE_TYPE: 'The certificate type does not exist.',
    UNKNOWN_CERTIFICATE: 'The certificate is not on this vessel.',
    UNKNOWN_ISSUANCE: 'The certificate issued on board is not on this vessel.',
    ISSUANCE_ALREADY_LINKED: 'The certificate issued on board is already linked to another record.',
    RENEWAL_CYCLE: 'A certificate cannot renew itself or one of its renewals.',
    CERTIFICATE_DELETED: 'The certificate has been removed.',
    UNKNOWN_USER: 'The person is not in the company.',
    UNKNOWN_EQUIPMENT: 'The equipment is not on this vessel.',
    UNKNOWN_WORK_RECORD: 'The work record is not on this vessel.',
    CE_NOT_CREDITED: 'The chief engineer survey was not credited for class.',
    CLASS_ITEM_MISMATCH: 'The item does not match the work record.',
    ALREADY_CONFIRMED: 'The survey is already confirmed.',
    RECORD_VOIDED: 'The work record has been voided.',
    CONFIRMED_BEFORE_SURVEY: 'The confirmation date is before the survey.',
    CE_SURVEY_NOT_ALLOWED: 'The item is not open to chief engineer survey.',
    CE_NOT_AUTHORIZED: 'There was no valid chief engineer authorization.',
    UNKNOWN_BV_ITEM: 'The equipment’s BV item code is not on the class item list.',
    ENDORSEMENT_OUTSIDE_WINDOW: 'The endorsement is outside the survey window.',
    UPLOAD_PURPOSE_MISMATCH: 'The upload is not a certificate page.',
    FILE_TYPE_MISMATCH: 'Only PDF, JPEG or PNG files.',
    FILE_TOO_LARGE: 'The file is too large.',
    EMPTY_FILE: 'The file is empty.',
    MISSING_COLUMN: 'A required column is missing.',
    FUTURE_DATE: 'The date cannot be in the future.',
    OFFICE_IS_WRITER: 'Certificates are entered by the office.',
    SHIP_IS_WRITER: 'This record is entered on board.',
    INVALID_NUMBER: 'Not a number.',
    IMPORT_HAS_ERRORS: 'The file has rows with errors; nothing was imported.',
    INVALID_FILE: 'The file could not be read.',
    FILE_NOT_ON_BOARD: 'The file is not on board yet.',
    UPLOAD_FAILED: 'The upload failed. Try again.',
    UNSUPPORTED_FILE_TYPE: 'Only PDF, JPEG or PNG files.',
    NOT_FOUND: 'The record was not found.',
    FORBIDDEN: 'You are not authorised for this action.',
    VALIDATION_FAILED: 'Some fields are invalid.',
    ROW_VERSION_CONFLICT: 'Someone else changed this record. Reload and try again.',
    NETWORK: 'No connection. Try again.',
    GENERIC: 'The operation failed. Try again.',
  },
};

export type VesselCertificatesResources = typeof vesselCertificatesEn;

export const vesselCertificatesTr: VesselCertificatesResources = {
  description: 'Geminin yasal, klas ve bayrak sertifikaları, sörveyleri ve klas kayıtları.',
  error: 'Sertifikalar okunamadı',
  retry: 'Yeniden dene',
  noVessel: 'Henüz gemi yok',
  categories: {
    statutory: 'Yasal Sertifikalar',
    class: 'Klas Sertifikaları',
    safety: 'Emniyet Sertifikaları',
    environmental: 'Çevre Sertifikaları',
    flag_port: 'Bayrak / Liman Sertifikaları',
    other: 'Diğer Sertifikalar',
  },
  badges: {
    solas: 'SOLAS',
    marpol: 'MARPOL',
    mlc: 'MLC',
    ism: 'ISM',
    isps: 'ISPS',
    environmental: 'Çevre',
    flag: 'Bayrak',
    class: 'Klas',
    other: 'Diğer',
  },
  status: {
    valid: 'Geçerli',
    expiring: 'Dolmak üzere',
    expired: 'Süresi geçmiş',
    superseded: 'Geçmişte kaldı',
    onBoard: 'Gemide düzenlendi',
  },
  tree: {
    root: 'Sertifika Takibi',
    label: 'Sertifika ağacı',
    open: 'Sertifika Takibi',
    count: '{{count}} sertifika',
    close: 'Kapat',
  },
  card: {
    vessel: 'Gemi',
    imo: 'IMO {{imo}}',
    vesselType: 'Gemi Tipi',
    flag: 'Bayrak',
    port: 'Liman',
    dateRange: 'Tarih Aralığı',
    dateRangePlaceholder: 'Her bitiş tarihi',
  },
  toolbar: {
    search: 'Sertifika, tür, durum ara...',
    searchLabel: 'Sertifika ara',
    noResults: 'Sertifika bulunamadı',
    add: 'Sertifika Ekle',
    issue: 'Gemide Düzenleneni Kaydet',
    export: 'Dışa Aktar',
    exportHint: 'Seçili kategorinin sertifikalarını Excel ya da CSV olarak aktar',
    exportFailed: 'Dosya hazırlanamadı.',
  },
  list: {
    title: 'Sertifika Listesi – {{scope}}',
    allScope: 'Bütün Sertifikalar',
    filters: 'Sertifika süzgeçleri',
    type: 'Sertifika Türü',
    statusFilter: 'Durum',
    due: 'Vade',
    all: 'Tümü',
    search: 'Ad ya da numara ara...',
    searchLabel: 'Listede ara',
    statuses: {
      all: 'Tümü',
      valid: 'Geçerli',
      expiring: 'Dolmak üzere',
      expired: 'Süresi geçmiş',
      superseded: 'Geçmişte kalan',
    },
    dueOptions: {
      all: 'Tümü',
      overdue: 'Süresi geçmiş',
      '30': 'Önümüzdeki 30 gün',
      '90': 'Önümüzdeki 90 gün',
      '365': 'Önümüzdeki 12 ay',
      none: 'Süresiz',
    },
    summary: {
      label: 'Sertifika özeti',
      valid: 'Geçerli',
      expiring: 'Dolmak üzere (≤ {{days}} gün)',
      overdue: 'Süresi geçmiş',
      total: 'Toplam',
    },
    columns: {
      no: 'No',
      name: 'Sertifika / Belge Adı',
      type: 'Tür',
      issuedBy: 'Düzenleyen',
      issueDate: 'Düzenlenme',
      expiryDate: 'Bitiş',
      status: 'Durum',
      daysLeft: 'Kalan Gün',
      actions: 'İşlemler',
    },
    table: 'Sertifika listesi',
    sortBy: '{{column}} sütununa göre sırala',
    daysLeft: '{{count}} gün kaldı',
    daysOverdue: '{{count}} gün geçti',
    noExpiry: 'Süresiz',
    pendingOffice: 'Gemide düzenlendi · ofis onayı bekleniyor',
    history_one: 'Geçmiş ({{count}})',
    history_other: 'Geçmiş ({{count}})',
    historyToggle_one: '{{name}} için önceki sertifikayı göster ({{count}})',
    historyToggle_other: '{{name}} için önceki {{count}} sertifikayı göster',
    showing: '{{total}} kayıttan {{from}} – {{to}}',
    showingNone: '0 kayıttan 0',
    pagination: 'Sertifika listesi sayfaları',
    pager: {
      first: 'İlk sayfa',
      previous: 'Önceki sayfa',
      next: 'Sonraki sayfa',
      last: 'Son sayfa',
    },
    view: '{{name}} görüntüle',
    edit: '{{name}} düzenle',
    more: '{{name}} için diğer işlemler',
    empty: 'Henüz sertifika yok',
    emptyOffice: 'Geminin sertifikalarını “Sertifika Ekle” ile girin.',
    emptyShip: 'Sertifikaları ofis girer; bir sonraki senkronla gelir.',
    emptyScope: 'Bu kategoride sertifika yok',
    noMatch: 'Süzgeçlere uyan sertifika yok',
  },
  upcoming: {
    title: 'Yaklaşan Sertifikalar',
    viewAll: 'Tümünü Gör',
    table: 'Yaklaşan sertifikalar',
    columns: {
      dueDate: 'Vade',
      name: 'Sertifika Adı',
      type: 'Tür',
      daysLeft: 'Kalan Gün',
      status: 'Durum',
    },
    kinds: {
      expiry: 'Bitiş',
      annual: 'Yıllık sörvey',
      intermediate: 'Ara sörvey',
      renewal: 'Yenileme sörveyi',
    },
    tones: {
      expiring: 'Dolmak üzere',
      expired: 'Süresi geçmiş',
      upcoming: 'Yaklaşıyor',
      open: 'Pencere açık',
      overdue: 'Gecikmiş',
    },
    empty: 'Önümüzdeki 90 günde vadesi gelen yok',
  },
  distribution: {
    title: 'Sertifika Durum Dağılımı',
    label: 'Sertifika durum dağılımı',
    total: 'Toplam',
    valid: 'Geçerli',
    expiring: 'Dolmak üzere',
    overdue: 'Süresi geçmiş',
    value: '{{value}} (%{{percent}})',
  },
  details: {
    title: 'Sertifika Ayrıntıları',
    empty: 'Ayrıntılarını görmek için bir sertifika seçin',
    daysLeft: 'Kalan Gün',
    tabs: {
      label: 'Sertifika ayrıntıları',
      general: 'Genel',
      document: 'Belge',
      history: 'Geçmiş',
      remarks: 'Notlar',
    },
    fields: {
      number: 'Sertifika No.',
      name: 'Sertifika Adı',
      type: 'Tür',
      subCategory: 'Alt Kategori',
      issuedBy: 'Düzenleyen',
      issueDate: 'Düzenlenme Tarihi',
      expiryDate: 'Bitiş Tarihi',
      anniversary: 'Yıl Dönümü',
      nextSurvey: 'Sıradaki Sörvey',
      window: 'Sörvey Penceresi',
      vessel: 'Gemi',
      status: 'Durum',
      daysLeft: 'Kalan Gün',
    },
    nextSurveyValue: '{{date}} · {{kind}}',
    windowValue: '{{from}} – {{to}}',
    windowStates: {
      upcoming: '{{date}} açılır',
      open: '{{date}} tarihine kadar açık',
      overdue: '{{date}} kapandı',
    },
    overridden: 'Ofisin girdiği tarih: {{reason}}',
    noExpiry: 'Süresiz',
    onBoardNote:
      'Sörveyör gemide düzenledi ve ofis onayına gönderildi. Ofis onaylayana kadar geçerli sayılır; onaydan sonra geminin sertifikası olur, yerine geçtiği sertifika Geçmiş’e iner.',
    supersededNote: 'Yenilendi ya da yerine yenisi düzenlendi: bu sertifika geçmiştedir, sayılmaz.',
    pages: {
      title: 'Taranmış sayfalar',
      page: 'Sayfa {{n}}',
      empty: 'Taranmış sayfa yok',
      view: '{{name}} görüntüle',
      download: '{{name}} indir',
      viewAll: 'Belgeyi görüntüle',
      onBoard: {
        on_board: 'Gemide',
        downloading: 'İniyor',
        not_on_board: 'Gemide değil',
      },
      queued: 'İndirme sırasında',
      queuedReason: 'İndirme sırasında: {{reason}}',
    },
    history: {
      chain: 'Yenileme zinciri',
      current: 'Bu sertifika',
      deleted: 'Silindi',
      endorsements: 'Vizeler',
      noEndorsements: 'Kayıtlı vize yok',
      endorsementLine: '{{kind}} · {{place}} · {{surveyor}}',
      satisfies: '{{kind}} penceresini karşılar ({{from}} – {{to}})',
      outside: 'Sörvey penceresinin dışında: kaydedildi, karşılamaz',
      additional: 'Ek sörvey',
      issuance: 'Gemide düzenlendi',
      issuanceLine: '{{number}} · {{issuedBy}} · {{date}} tarihine kadar',
      shortTerm: 'Kısa süreli',
      audit: 'Denetim kaydı',
      openAudit: 'Denetim kaydını göster',
      auditTitle: 'Denetim kaydı — {{name}}',
      officeHistory: 'Bu sertifikanın alan geçmişi ofistedir.',
    },
    remarks: {
      empty: 'Henüz not yok',
      by: '{{person}} · {{date}}',
    },
  },
  attached: {
    title: 'Ekli Belgeler',
    empty: 'Taranmış sayfa yok',
    download: '{{name}} indir',
  },
  quickActions: {
    title: 'Hızlı İşlemler',
    viewDocument: 'Belgeyi Görüntüle',
    download: 'İndir',
    addRemark: 'Not Ekle',
    endorse: 'Vize Kaydet',
    reminder: 'Hatırlatıcı',
    approve: 'İncele ve onayla',
  },
  approval: {
    title: 'Gemide düzenlenen sertifikalar',
    officeTitle_one: 'Gemide düzenlenen {{count}} sertifika ofis onayı bekliyor',
    officeTitle_other: 'Gemide düzenlenen {{count}} sertifika ofis onayı bekliyor',
    shipTitle_one: 'Gemide düzenlenen {{count}} sertifika · ofis onayına gönderildi',
    shipTitle_other: 'Gemide düzenlenen {{count}} sertifika · ofis onayına gönderildi',
    shipNote: 'Ofis onayına gönderildi',
    noRight: 'Onay için sertifika ekleme yetkisi gerekir',
    review: 'İncele ve onayla',
    dialogTitle: 'Gemide düzenlenen sertifikayı onayla',
    approve: 'Onayla',
    approved: 'Onaylandı: {{name}} sertifika listesinde.',
    hint: 'Bu kayıttan ofis sertifikası oluşturulur ve gemiye gönderilir. Sonra düzenleyebilirsiniz.',
    hintReplaces:
      'Bu kayıttan ofis sertifikası oluşturulur ve gemiye gönderilir; yerine geçtiği sertifika Geçmiş’e iner (silinmez). Sonra düzenleyebilirsiniz.',
    fields: {
      name: 'Sertifika',
      number: 'Numara',
      issuedBy: 'Düzenleyen',
      issueDate: 'Düzenleme Tarihi',
      expiryDate: 'Bitiş Tarihi',
      replaces: 'Yerine geçtiği',
    },
    bell: 'Gemide düzenlenen sertifika',
  },
  reminder: {
    title: 'Hatırlatmalar',
    body: 'Hatırlatmalar kendiliğindendir: bitişten 90, 60 ve 30 gün önce bildirim gider. Kişisel hatırlatıcı kullanılmaz.',
    stages: 'Bildirim kademeleri',
    stage: 'Bitişten {{days}} gün önce',
    reached: 'Ulaşıldı',
    pending: 'Bekliyor',
    expired: 'Sertifikanın süresi geçti: süresi geçmiş bildirimi etkin.',
    none: 'Henüz bildirim yok ({{days}} gün kaldı).',
    current: 'Şu anki kademe: bitişten {{days}} gün önce.',
    noExpiry: 'Bu sertifikanın bitiş tarihi yok.',
    close: 'Kapat',
  },
  quickLinks: {
    title: 'Hızlı Bağlantılar',
    add: 'Yeni Sertifika Ekle',
    issue: 'Gemide Düzenleneni Kaydet',
    calendar: 'Sertifika Takvimi',
    surveys: 'Sörvey Planı',
    regulatory: 'Mevzuat Gereklilikleri',
  },
  views: {
    back: 'Sertifika Listesine dön',
  },
  viewer: {
    title: '{{name}} — sayfa {{page}} / {{count}}',
    previous: 'Önceki sayfa',
    next: 'Sonraki sayfa',
    close: 'Kapat',
    notOnBoard: 'Bu sayfa henüz gemide değil. İndirme sırasıyla iner.',
    printFooter:
      'Bilgi kopyası — {{name}} No. {{number}} — Felixmate — {{at}} UTC, {{person}}, {{place}}',
  },
  surveys: {
    title: 'Sörvey Planı',
    table: 'Sörvey planı',
    columns: {
      survey: 'Sörvey',
      last: 'Son',
      next: 'Sıradaki',
      window: 'Pencere',
      status: 'Durum',
      report: 'Rapor',
      actions: 'İşlemler',
    },
    kinds: {
      annual: 'Yıllık',
      intermediate: 'Ara',
      special: 'Özel (Yenileme)',
      bottom: 'Havuz',
      boiler: 'Kazan',
      safety_equipment: 'Emniyet Teçhizatı',
      radio: 'Telsiz',
      tailshaft: 'Pervane Şaftı',
      other: 'Diğer',
    },
    states: {
      upcoming: 'Yaklaşıyor',
      open: 'Pencere açık',
      overdue: 'Gecikmiş',
    },
    notRecorded: 'Kayıt yok',
    reportOnFile: 'Dosyada',
    overridden: 'Pencere klas durum raporundan: {{reason}}',
    add: 'Sörvey Ekle',
    edit: '{{kind}} sörveyini düzenle',
    addKind: '{{kind}} sörveyi ekle',
  },
  regulatory: {
    title: 'Mevzuat Gereklilikleri',
    tabs: {
      label: 'Mevzuat gereklilikleri',
      findings: 'Klas Şartları',
      items: 'Klas Kalem Listesi',
      due: 'Klas Vade Listesi',
      authorizations: 'BM Yetkileri',
    },
    findings: {
      table: 'Klas bulguları',
      columns: {
        number: 'No',
        kind: 'Tür',
        text: 'Açıklama',
        equipment: 'Ekipman',
        imposed: 'Verildi',
        due: 'Vade',
        closed: 'Kapandı',
        status: 'Durum',
        actions: 'İşlemler',
      },
      kinds: {
        coc: 'Klas Şartı',
        memorandum: 'Memorandum',
        recommendation: 'Tavsiye',
      },
      states: { open: 'Açık', closed: 'Kapandı', overdue: 'Gecikmiş' },
      add: 'Bulgu Ekle',
      edit: '{{number}} düzenle',
      empty: 'Klas bulgusu yok',
    },
    items: {
      table: 'Klas kalem listesi',
      columns: {
        code: 'Kalem Kodu',
        description: 'Açıklama',
        category: 'Kategori',
        principle: 'Sörvey Prensibi',
        interval: 'Aralık (ay)',
        ce: 'Başmühendis sörveyi',
      },
      categories: {
        machinery: 'Makine',
        electrical: 'Elektrik',
        pressure_vessel: 'Basınçlı kap',
        boiler: 'Kazan',
        tailshaft: 'Pervane şaftı',
        other: 'Diğer',
      },
      principles: {
        pms_ce: 'PMS (başmühendis)',
        surveyor: 'Sörveyör',
        condition_monitoring: 'Durum izleme',
        none: 'Yok',
      },
      ceAllowed: 'Açık',
      ceNotAllowed: 'Açık değil',
      warnings: 'BV kalem kodu listede olmayan ekipman: {{codes}}',
      import: 'Kalem listesini içe aktar',
      empty: 'Klas kalemi yok',
      emptyHint: 'BV kalem listesini içe aktarın (CSV ya da XLSX).',
    },
    due: {
      table: 'Klas vade listesi',
      columns: {
        item: 'Kalem',
        equipment: 'Ekipman',
        lastSurvey: 'Son BM sörveyi',
        confirmation: 'Sörveyör teyidi',
        due: 'Sıradaki vade',
        state: 'Durum',
      },
      states: {
        never_surveyed: 'Sörvey yok',
        not_credited: 'Sayılmadı',
        awaiting_confirmation: 'Teyit bekliyor',
        confirmed: 'Teyitli',
        overdue: 'Gecikmiş',
      },
      notCredited: 'Sayılmadı: {{reason}}',
      confirmedBy: '{{date}} · {{surveyor}}',
      confirm: 'Sörveyör Teyidini Kaydet',
      empty: 'Klas kalemi yok',
    },
    authorizations: {
      table: 'Başmühendis yetki beyanları',
      columns: {
        person: 'Başmühendis',
        from: 'Başlangıç',
        to: 'Bitiş',
        ref: 'Beyan Ref.',
        state: 'Durum',
        actions: 'İşlemler',
      },
      valid: 'Geçerli',
      notValid: 'Geçersiz',
      openEnded: 'Süresiz',
      unknownPerson: 'Henüz gemide değil',
      add: 'Yetki Ekle',
      edit: '{{ref}} yetkisini düzenle',
      empty: 'Yetki beyanı yok',
    },
  },
  calendar: {
    title: 'Sertifika Takvimi',
    previous: 'Önceki ay',
    next: 'Sonraki ay',
    today: 'Bugün',
    grid: 'Sertifika takvimi',
    legend: { expiry: 'Bitiş', survey: 'Sörvey vadesi' },
    agenda: 'Bu ay vadesi gelenler',
    empty: 'Bu ay vadesi gelen yok',
    weekdays: {
      mon: 'Pzt',
      tue: 'Sal',
      wed: 'Çar',
      thu: 'Per',
      fri: 'Cum',
      sat: 'Cmt',
      sun: 'Paz',
    },
    dayEvents: '{{count}} vade',
  },
  dialogs: {
    cancel: 'Vazgeç',
    close: 'Kapat',
    immutable: 'Bu kayıt kaydedildikten sonra değiştirilemez.',
    endorsement: {
      title: 'Vize Kaydet',
      certificate: 'Sertifika',
      kind: 'Sörvey',
      kinds: { annual: 'Yıllık', intermediate: 'Ara', additional: 'Ek' },
      endorsedOn: 'Vize Tarihi',
      place: 'Yer',
      surveyor: 'Sörveyör',
      remarks: 'Notlar',
      scan: 'Tarama (isteğe bağlı)',
      save: 'Vizeyi Kaydet',
      outside: 'Sörvey penceresinin dışında kaydedildi: sörveyi karşılamaz.',
      saved: 'Vize kaydedildi.',
    },
    confirmation: {
      title: 'Sörveyör Teyidini Kaydet',
      surveyor: 'Sörveyör',
      confirmedOn: 'Teyit Tarihi',
      items: 'Kalemler',
      itemOption: '{{code}} — {{description}} (BM sörveyi {{date}})',
      noItems: 'Teyit bekleyen başmühendis sörveyi yok.',
      remarks: 'Notlar',
      scan: 'İmzalı rapor (isteğe bağlı)',
      save: 'Teyidi Kaydet',
    },
    issuance: {
      title: 'Gemide Düzenleneni Kaydet',
      hint: 'Sörveyör bu sertifikayı gemide düzenledi. Ofis kaydedene kadar geçerli sayılır.',
      type: 'Sertifika Türü',
      replaces: 'Yerine geçtiği',
      replacesNone: 'Yok (yeni sertifika)',
      number: 'Sertifika No.',
      issuedBy: 'Düzenleyen',
      issueDate: 'Düzenlenme Tarihi',
      expiryDate: 'Bitiş Tarihi',
      shortTerm: 'Kısa süreli / geçici sertifika',
      remarks: 'Notlar',
      scan: 'Tarama (isteğe bağlı)',
      save: 'Kaydet',
    },
    scanFailed: 'Kayıt yazıldı ama tarama eklenemedi: {{reason}}',
    remark: {
      title: 'Not Ekle',
      text: 'Not',
      hint: 'Not bugünün tarihi ve adınızla kaydedilir. Önceki notlar değiştirilemez.',
      save: 'Not Ekle',
    },
    certificate: {
      addTitle: 'Sertifika Ekle',
      editTitle: 'Sertifikayı Düzenle',
      sections: {
        identity: 'Sertifika',
        dates: 'Tarihler',
        survey: 'Sıradaki sörvey (ofisin girdiği)',
        links: 'Bağlantılar',
        pages: 'Taranmış sayfalar',
      },
      type: 'Sertifika Türü',
      number: 'Sertifika No.',
      name: 'Sertifika Adı',
      issuedBy: 'Düzenleyen',
      issueDate: 'Düzenlenme Tarihi',
      expiryDate: 'Bitiş Tarihi',
      anniversaryDate: 'Yıl Dönümü',
      anniversaryHint: 'Boşsa bitiş tarihinin gün ve ayı.',
      nextSurveyOverride: 'Sıradaki Sörvey',
      windowFromOverride: 'Pencere Başı',
      windowToOverride: 'Pencere Sonu',
      overrideReason: 'Gerekçe',
      overrideHint:
        'Hesaplanan pencere için boş bırakın. Klas ya da bayrak durum raporundaki tarih gerekçe ister.',
      renewedFrom: 'Yenilediği',
      renewedNone: 'Yok',
      onBoard: 'Gemide düzenlenen',
      onBoardNone: 'Yok',
      pagesHint: 'PDF, JPEG ya da PNG; sayfa başına en çok 25 MB.',
      pagesAfterSave: 'Önce sertifikayı kaydedin, sonra taranmış sayfalarını ekleyin.',
      upload: 'Sayfa ekle',
      uploading: '{{name}} yükleniyor (%{{percent}})',
      moveUp: '{{name}} yukarı taşı',
      moveDown: '{{name}} aşağı taşı',
      removePage: '{{name}} kaldır',
      save: 'Kaydet',
      remove: 'Sertifikayı kaldır',
      removeTitle: 'Sertifikayı kaldır',
      removeBody: '{{name}} kaldırılsın mı? Geçmişte ve denetim kaydında kalır.',
      saved: 'Sertifika kaydedildi. Şimdi taranmış sayfalarını ekleyebilirsiniz.',
    },
    survey: {
      addTitle: 'Sörvey Ekle',
      editTitle: 'Sörveyi Düzenle',
      kind: 'Sörvey',
      lastDoneOn: 'Son Yapılış',
      nextDueOn: 'Sıradaki Vade',
      windowFrom: 'Pencere Başı',
      windowTo: 'Pencere Sonu',
      computed: 'Hesaplanan pencere: {{from}} – {{to}}',
      overrideReason: 'Gerekçe',
      overrideHint: 'Hesaplanandan farklı pencere gerekçe ister.',
      remarks: 'Notlar',
      save: 'Kaydet',
      remove: 'Sörveyi kaldır',
    },
    finding: {
      addTitle: 'Klas Bulgusu Ekle',
      editTitle: 'Klas Bulgusunu Düzenle',
      kind: 'Tür',
      number: 'No',
      text: 'Açıklama',
      imposedOn: 'Verildiği Tarih',
      dueOn: 'Vade',
      closedOn: 'Kapandığı Tarih',
      equipment: 'Ekipman',
      equipmentNone: 'Bağlı değil',
      equipmentHint: 'Açık ve vadesi geçmiş klas şartı ekipmanı PMS dışı yapar.',
      save: 'Kaydet',
      remove: 'Bulguyu kaldır',
    },
    authorization: {
      addTitle: 'BM Yetkisi Ekle',
      editTitle: 'BM Yetkisini Düzenle',
      hint: 'Armatörün başmühendise makine kalemlerini sörvey etme yetkisi veren beyanı [NR467 Pt A Ch 2 App 2].',
      person: 'Başmühendis',
      validFrom: 'Başlangıç',
      validTo: 'Bitiş',
      declarationRef: 'Beyan Ref.',
      save: 'Kaydet',
      remove: 'Yetkiyi kaldır',
    },
    import: {
      title: 'Klas Kalem Listesini İçe Aktar',
      hint: 'Item Code, Description, Category, Survey Principle ve Interval (months) sütunlu CSV ya da XLSX.',
      file: 'Dosya',
      choose: 'Dosya seç',
      preview: 'Önizle',
      apply: 'İçe aktar',
      rows: 'Okunan satır: {{count}}',
      added: 'Eklenecek: {{count}}',
      updated: 'Değişecek: {{count}}',
      unchanged: 'Aynı: {{count}}',
      tiles: {
        rows: 'Okunan satır',
        added: 'Eklenecek',
        updated: 'Değişecek',
        unchanged: 'Aynı',
        errors: 'Hatalı satır',
      },
      missing: 'Dosyada olmayan (korunur): {{codes}}',
      errorsTitle: 'Hatalı satırlar: düzeltilene kadar hiçbir şey aktarılmaz.',
      errorRow: 'Satır {{row}}',
      errorColumns: { row: 'Satır', column: 'Sütun', problem: 'Sorun' },
      addedColumns: { row: 'Satır', code: 'Kalem Kodu', description: 'Açıklama' },
      applied: 'Aktarıldı: {{added}} eklendi, {{updated}} değişti.',
    },
  },
  fieldErrors: {
    REQUIRED: 'Zorunlu',
    TOO_LONG: 'Çok uzun',
    INVALID_DATE: 'Geçerli bir tarih girin',
    FUTURE_DATE: 'Gelecekte olamaz',
    EXPIRY_BEFORE_ISSUE: 'Bitiş tarihi düzenlenme tarihinden önce',
    OVERRIDE_REASON_REQUIRED: 'Gerekçeyi girin (klas ya da bayrak durum raporu)',
    WINDOW_ORDER: 'Pencere başlamadan bitiyor',
    DUE_BEFORE_LAST: 'Sıradaki vade son sörveyden önce',
    DUE_BEFORE_IMPOSED: 'Vade verildiği tarihten önce',
    CLOSED_BEFORE_IMPOSED: 'Kapanış verildiği tarihten önce',
    VALID_TO_BEFORE_FROM: 'Bitiş başlangıçtan önce',
    ITEMS_REQUIRED: 'En az bir kalem seçin',
  },
  errors: {
    INVALID_CERTIFICATE_TYPE: 'Sertifika türü geçersiz.',
    INVALID_CERTIFICATE: 'Sertifikanın bazı alanları geçersiz.',
    INVALID_CERTIFICATE_PAGE: 'Taranmış sayfa geçersiz.',
    INVALID_SURVEY: 'Sörveyin bazı alanları geçersiz.',
    INVALID_CLASS_FINDING: 'Klas bulgusunun bazı alanları geçersiz.',
    INVALID_CLASS_ITEM: 'Klas kaleminin bazı alanları geçersiz.',
    INVALID_CE_AUTHORIZATION: 'Yetki beyanının bazı alanları geçersiz.',
    INVALID_ENDORSEMENT: 'Vizenin bazı alanları geçersiz.',
    INVALID_CLASS_CONFIRMATION: 'Teyidin bazı alanları geçersiz.',
    INVALID_ISSUANCE: 'Gemide düzenlenen sertifikanın bazı alanları geçersiz.',
    PAGE_FILE_FIXED: 'Sayfanın dosyası değişmez; yeni taramayı yeni sayfa olarak ekleyin.',
    CERTIFICATE_TYPE_IN_USE: 'Türün sertifikaları var; kaldırılamaz.',
    EXPIRY_BEFORE_ISSUE: 'Bitiş tarihi düzenlenme tarihinden önce.',
    OVERRIDE_REASON_REQUIRED: 'Hesaplanan pencereden farklı tarih gerekçe ister.',
    WINDOW_ORDER: 'Pencere başlamadan bitiyor.',
    DUE_BEFORE_LAST: 'Sıradaki vade son sörveyden önce.',
    DUE_BEFORE_IMPOSED: 'Vade verildiği tarihten önce.',
    CLOSED_BEFORE_IMPOSED: 'Kapanış verildiği tarihten önce.',
    VALID_TO_BEFORE_FROM: 'Geçerlilik sonu başlangıcından önce.',
    DUPLICATE_TYPE_CODE: 'Bu tür kodu kullanılıyor.',
    DUPLICATE_ITEM_CODE: 'Bu kalem kodu listede var.',
    DUPLICATE_ITEM: 'Aynı sörvey iki kez seçildi.',
    UNKNOWN_CERTIFICATE_TYPE: 'Sertifika türü yok.',
    UNKNOWN_CERTIFICATE: 'Sertifika bu gemide değil.',
    UNKNOWN_ISSUANCE: 'Gemide düzenlenen sertifika bu gemide değil.',
    ISSUANCE_ALREADY_LINKED: 'Gemide düzenlenen sertifika başka bir kayda bağlı.',
    RENEWAL_CYCLE: 'Sertifika kendisini ya da yenilemelerinden birini yenileyemez.',
    CERTIFICATE_DELETED: 'Sertifika kaldırılmış.',
    UNKNOWN_USER: 'Kişi şirkette yok.',
    UNKNOWN_EQUIPMENT: 'Ekipman bu gemide değil.',
    UNKNOWN_WORK_RECORD: 'İş kaydı bu gemide değil.',
    CE_NOT_CREDITED: 'Başmühendis sörveyi klas için sayılmadı.',
    CLASS_ITEM_MISMATCH: 'Kalem iş kaydıyla uyuşmuyor.',
    ALREADY_CONFIRMED: 'Sörvey zaten teyit edildi.',
    RECORD_VOIDED: 'İş kaydı iptal edilmiş.',
    CONFIRMED_BEFORE_SURVEY: 'Teyit tarihi sörveyden önce.',
    CE_SURVEY_NOT_ALLOWED: 'Kalem başmühendis sörveyine açık değil.',
    CE_NOT_AUTHORIZED: 'Geçerli başmühendis yetki beyanı yoktu.',
    UNKNOWN_BV_ITEM: 'Ekipmanın BV kalem kodu klas kalem listesinde yok.',
    ENDORSEMENT_OUTSIDE_WINDOW: 'Vize sörvey penceresinin dışında.',
    UPLOAD_PURPOSE_MISMATCH: 'Yükleme bir sertifika sayfası değil.',
    FILE_TYPE_MISMATCH: 'Yalnız PDF, JPEG ya da PNG dosyası.',
    FILE_TOO_LARGE: 'Dosya çok büyük.',
    EMPTY_FILE: 'Dosya boş.',
    MISSING_COLUMN: 'Zorunlu bir sütun yok.',
    FUTURE_DATE: 'Tarih gelecekte olamaz.',
    OFFICE_IS_WRITER: 'Sertifikaları ofis girer.',
    SHIP_IS_WRITER: 'Bu kayıt gemide girilir.',
    INVALID_NUMBER: 'Sayı değil.',
    IMPORT_HAS_ERRORS: 'Dosyada hatalı satırlar var; hiçbir şey aktarılmadı.',
    INVALID_FILE: 'Dosya okunamadı.',
    FILE_NOT_ON_BOARD: 'Dosya henüz gemide değil.',
    UPLOAD_FAILED: 'Yükleme başarısız. Yeniden deneyin.',
    UNSUPPORTED_FILE_TYPE: 'Yalnız PDF, JPEG ya da PNG dosyası.',
    NOT_FOUND: 'Kayıt bulunamadı.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    VALIDATION_FAILED: 'Bazı alanlar geçersiz.',
    ROW_VERSION_CONFLICT: 'Kaydı başka biri değiştirdi. Yeniden yükleyip deneyin.',
    NETWORK: 'Bağlantı yok. Yeniden deneyin.',
    GENERIC: 'İşlem yapılamadı. Yeniden deneyin.',
  },
};
